import { Injectable } from '@angular/core';
import { Conversation, Message, User } from '../models';
import { HttpClient } from '@angular/common/http';
import { AppService } from './app.service';
import { Subject, Subscription } from 'rxjs';
import { CounterService } from './counter.service';
import { DatabaseService } from './database.service';
import { ModalController } from '@ionic/angular';
import { safeFind, safeFindIndex } from 'src/app/utils';
import { HelperService, LocalAttachment, StorageService } from '.';
import { ListenEvent } from '@angular/fire/database';
import { ChatPage } from 'src/app/pages/chat/chat.page';
import { UserService } from './user.service';
import { OrganizationService } from './organization.service';
import { environment } from 'src/environments/environment';

export interface IChatState {
  mode: "messages" | "conversation",
  id: string;
  options?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {

  public newConversation: Conversation;
  public userConversationsInitialized = false;
  public userConversationListSubscription: Subscription;
  private userConversations: Conversation[] = [];
  /** Map of normalized member set (sorted IDs joined by comma) to conversation id for O(1) lookup */
  private memberKeyToConversationId = new Map<string, string>();
  public userConversationSubject = new Subject<any>();

  private getMemberKey(memberIds: string[]): string {
    return (memberIds || []).slice().sort().join(',');
  }

  private updateMemberMap(convo: Conversation, add: boolean): void {
    const members = convo.members && typeof convo.members === 'object' && !Array.isArray(convo.members)
      ? Object.keys(convo.members)
      : (Array.isArray(convo.members) ? convo.members : []);
    const key = this.getMemberKey(members);
    if (add) {
      this.memberKeyToConversationId.set(key, convo.id);
    } else {
      this.memberKeyToConversationId.delete(key);
    }
  }

  constructor(
    private appService: AppService,
    private counterService: CounterService,
    private dbService: DatabaseService,
    private helperService: HelperService,
    private http: HttpClient,
    private modalCtrl: ModalController,
    private organizationService: OrganizationService,
    private storageService: StorageService,
    private userService: UserService,
  ) { }

  public async loadUserConversations(userId): Promise<void> {
    const convos = await this.getUserConversationsAsPromise(userId);
    if(convos) {
      this.userConversations.push(...convos);
      convos.forEach((c) => this.updateMemberMap(c, true));
    }
    this.userConversationsInitialized = true;
    this.publishUserConversationSubject("loaded", this.userConversations);
    this.countUnread();
    this.userConversationListSubscription = this.dbService.getStateChangesByRef(`userConversations/${userId}`, [ListenEvent.added, ListenEvent.changed, ListenEvent.removed]).subscribe((action) => {
      console.log("user convo changed");
      // Added
      if(action.event === ListenEvent.added) {
        const key = action.snapshot.key;
        const payload: any = action.snapshot.val();
        if(key && safeFindIndex(this.userConversations, conv => conv.id === key) === -1) {
          const convo = new Conversation({id: key, ...payload});
          this.userConversations.push(convo);
          this.updateMemberMap(convo, true);
          this.publishUserConversationSubject("child_added", convo);
          this.countUnread();
        }
      }
      // Updated
      if(action.event === ListenEvent.changed) {
        const key = action.snapshot.key;
        const payload: any = action.snapshot.val();
        const index = safeFindIndex(this.userConversations, conv => conv.id === key);
        if(index > -1) {
          this.updateMemberMap(this.userConversations[index], false);
          const convo = new Conversation({id: key, ...payload});
          this.userConversations[index] = convo;
          this.updateMemberMap(convo, true);
          this.publishUserConversationSubject("child_changed", convo);
          this.countUnread();
        }
      }
      // Removed
      if(action.event === ListenEvent.removed) {
        const key = action.snapshot.key;
        const index = safeFindIndex(this.userConversations, conv => conv.id === key);
        if(index > -1) {
          this.updateMemberMap(this.userConversations[index], false);
          this.userConversations.splice(index, 1);
          this.publishUserConversationSubject("child_removed", key);
          this.countUnread();
        }
      }
    });
  }

  private countUnread() {
    let unread = 0;
    this.userConversations.forEach((convo) => {
      if(convo.read === false) {
        unread++;
      }
    });
    this.counterService.setCounter("unreadConversations", unread);
  }

  private publishUserConversationSubject(state: string, data: any) {
    this.userConversationSubject.next({state: state, data: data});
  }

  /** Returns the same array reference (no copy). Use for list display; do not mutate. */
  public getUserConversationsRef(): Conversation[] {
    return this.userConversations;
  }

  public getUserConversations() {
    const convos = this.helperService.copyObject(this.userConversations);
    return convos?.length > 0? convos.map(convo => {return new Conversation(convo)}) : null
  }

  public getUserConversationSubject(): Subject<any> {
    return this.userConversationSubject;
  }

  public async getUserConversationsAsPromise(userId: string): Promise<any> {
    const snap = await this.dbService.getSnapshot(`userConversations/${userId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(convo => {return new Conversation(convo)}) : null;
  }

  public clearUserConversations() {
    this.userConversations = [];
    this.memberKeyToConversationId.clear();
    this.userConversationsInitialized = false;
    if(this.userConversationListSubscription) {
      this.userConversationListSubscription.unsubscribe();
    }
  }

  // Conversations
  createConversation(conversation: Partial<Conversation>): Promise<any> {
    let conversationUrl = `${this.appService.getApiURL()}/conversations`;
    return this.http.post(conversationUrl, conversation).toPromise();
  }

  getConversationRef(id: string) {
    return this.dbService.getObjectByRef<Conversation>(`conversations/${id}`, "id");
  }

  getUserConversationRef(userId: string, conversationId: string) {
    return this.dbService.getObjectByRef<Conversation>(`userConversations/${userId}/${conversationId}`, "id");
  }

  async getConversationAsPromise(id: string): Promise<Conversation> {
    const snap = await this.dbService.getSnapshot(`conversations/${id}`);
    return snap? new Conversation({
      id: id,
      ...snap
    }) : null;
  }

  async getUserConversationAsPromise(userId: string, conversationId: string): Promise<Conversation> {
    const snap = await this.dbService.getSnapshot(`userConversations/${userId}/${conversationId}`);
    return snap? new Conversation({
      id: conversationId,
      ...snap
    }) : null;
  }

  addMemberToConversation(conversationId: string, memberId: string, memberObject: {name: string}) {
    return this.dbService.setRef(`conversations/${conversationId}/members/${memberId}`, memberObject);
  }

  addMembersToConversation(conversationId: string, memberObject: {[userId: string]: {name: string}}) {
    return this.dbService.updateRef(`conversations/${conversationId}/members`, memberObject);
  }

  removeMemberFromConversation(conversationId: string, userId: string) {
    return this.dbService.removeRef(`conversations/${conversationId}/members/${userId}`);
  }

  updateConversation(id: string, data: Partial<Conversation>): Promise<void> {
    return this.dbService.updateRef(`conversations/${id}`, data);
  }

  deleteConversation(id: string): Promise<void> {
    return this.dbService.removeRef(`conversations/${id}`);
  }

  markConversationAsRead(userId: string, conversationId: string): Promise<void> {
    return this.dbService.setRef(`/userConversations/${userId}/${conversationId}/read`, true);
  }

  markConversationAsUnread(userId: string, conversationId: string): Promise<void> {
    return this.dbService.setRef(`/userConversations/${userId}/${conversationId}/read`, false);
  }

  // Messages
  getMessagesList(conversationId: string) {
    return this.dbService.getListByRefTest<Message>(`messages/${conversationId}`);
  }

  deleteMessage(conversationId: string, messageId: string): Promise<void> {
    return this.dbService.removeRef(`/messages/${conversationId}/${messageId}`);
  }

  addMessageToConversation(conversationId: string, message: Partial<Message>) {
    return this.dbService.pushByRef(`messages/${conversationId}`, message);
  }

  updateMessage(conversationId: string, messageId: string, message: Partial<Message>) {
    return this.dbService.updateRef(`messages/${conversationId}/${messageId}`, message)
  }

  async addAttachmentToConversation (
    user: User,
    conversationId: string,
    attachment: LocalAttachment,
    replyTo?: Message['replyTo']
  ): Promise<any> {

    const storagePath = `conversations/${conversationId}/${attachment.name}`;
    let attachmentRef: string;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }
     
    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error("Failed to upload attachment");
    }
    
    return this.dbService.pushByRef(`/messages/${conversationId}`, {
      id: null,
      message: "",        
      createdBy: user.uid,
      creatorName: user.getFullName(),
      creationDate: new Date().toISOString(),
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }],
      ...(replyTo ? { replyTo } : {})
    })
  }

  async copyAttachmentToConversation (
    user: User,
    conversationId: string,
    attachment: string,
    type: "image" | "video" | "audio" | "file",
    fileName: string,
    message?: string,
    replyTo?: Message['replyTo']
  ): Promise<any> {
    return this.dbService.pushByRef(`/messages/${conversationId}`, {
      id: null,
      message: message || "",        
      createdBy: user.uid,
      creatorName: user.getFullName(),
      creationDate: new Date().toISOString(),
      attachments: [{
        url: attachment,
        fileName: fileName,
        type: type,
      }],
      ...(replyTo ? { replyTo } : {})
    })
  }

  async openChatPanel(state?: IChatState) {
    const modal = await this.modalCtrl.create({
      component: ChatPage,
      cssClass: 'modal-chat-overlay',
      backdropDismiss: true,
      showBackdrop: true,
      animated: true,
      backdropBreakpoint: 992,
      componentProps: {
        state: state
      }
    });
    await modal.present();
  }

  checkIfChatExists(memberIds: string[]): string {
    return this.memberKeyToConversationId.get(this.getMemberKey(memberIds)) ?? undefined;
  }

  async startSupportChatWithMessage(message: string): Promise<void> {
    const user = this.userService.getAppUser();
    if (!user?.uid || !environment.supportUserId) {
      return;
    }

    const memberIds = [environment.supportUserId, user.uid];
    let conversationId = this.checkIfChatExists(memberIds);

    if (!conversationId) {
      this.newConversation = new Conversation({ members: memberIds });
      const organization = this.organizationService.getAppOrganization();
      conversationId = await this.createConversation({
        ...this.newConversation,
        ...(organization?.uid ? { organization: organization.uid } : {}),
      });
    }

    await this.addMessageToConversation(conversationId, {
      id: null,
      message,
      createdBy: user.uid,
      creatorName: user.getFullName(),
      creationDate: new Date().toISOString(),
    });

    await this.openChatPanel({
      mode: 'conversation',
      id: conversationId,
    });
  }

  navigateToConversation(memberIds: string[], options: {bulkCreate?: boolean} = {}) {
    let existingConvo: string = null;

    // We skip the conversation check when bulk creating
    if(!options.bulkCreate) {
      existingConvo = this.checkIfChatExists(memberIds)
    }

    console.log("existingConvo", existingConvo);

    if(existingConvo) {
      this.openChatPanel({
        mode: "conversation",
        id: existingConvo
      });
    } else {
      this.newConversation = new Conversation({
        members: memberIds
      });
      this.openChatPanel({
        mode: "conversation",
        id: "new",
        options: options
      });
    }
  }

  openConversation(conversationId: string) {
    if(safeFind(this.userConversations, conv => conv.id === conversationId)) {
      this.openChatPanel({
        mode: "conversation",
        id: conversationId
      });
    }
  }
}
